import { useMemo, useState } from "react";
import { analyze } from "../vault/health";
import type { DecryptedItem } from "../vault/items";
import { useT } from "../i18n";
import { ShieldCheck, TriangleAlert, Recycle, Check } from "lucide-react";

// Security health report (M5.1): weak + reused passwords, computed client-side from already-
// decrypted items. By default scans the open vault; "scan all vaults" loads the rest on demand.
export function SecurityHealthPanel({
  items,
  scanAll,
  onSelect,
  onClose,
}: {
  items: DecryptedItem[];
  scanAll: () => Promise<DecryptedItem[]>;
  onSelect: (itemId: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [scope, setScope] = useState<DecryptedItem[]>(items);
  const [allScanned, setAllScanned] = useState(false);
  const [busy, setBusy] = useState(false);
  const report = useMemo(() => analyze(scope), [scope]);

  async function doScanAll() {
    setBusy(true);
    try {
      setScope(await scanAll());
      setAllScanned(true);
    } finally {
      setBusy(false);
    }
  }

  const pick = (id: string) => {
    onSelect(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50" onClick={onClose}>
      <div className="flex max-h-[80vh] w-[560px] flex-col rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <ShieldCheck className="h-4 w-4" />
          <div className="font-semibold">{t("Security health")}</div>
          <button onClick={onClose} className="ml-auto rounded border border-border px-2 py-1 text-xs hover:border-accent">
            {t("Close")}
          </button>
        </div>

        <div className="flex items-center gap-4 border-b border-border px-5 py-3 text-sm">
          <span className="text-muted">{report.passwords} {t("passwords")} · {report.scannedItems} {t("items")}</span>
          <span className={`flex items-center gap-1 ${report.weak.length ? "text-danger" : "text-ok"}`}><TriangleAlert className="h-3.5 w-3.5" /> {report.weak.length} {t("weak")}</span>
          <span className={`flex items-center gap-1 ${report.reused.length ? "text-danger" : "text-ok"}`}><Recycle className="h-3.5 w-3.5" /> {report.reused.length} {t("reused")}</span>
          {!allScanned && (
            <button onClick={doScanAll} disabled={busy} className="ml-auto rounded border border-border px-2 py-1 text-xs hover:border-accent disabled:opacity-60">
              {busy ? "…" : t("Scan all vaults")}
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-3 text-sm">
          {report.weak.length === 0 && report.reused.length === 0 && (
            <div className="flex items-center justify-center gap-1.5 py-6 text-center text-ok"><Check className="h-4 w-4" /> {t("No weak or reused passwords found.")}</div>
          )}

          {report.weak.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 text-xs font-semibold tracking-wide text-muted">{t("WEAK PASSWORDS")}</div>
              {report.weak.map((w, i) => (
                <button key={i} onClick={() => pick(w.itemId)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-panel2">
                  <span className="text-danger"><TriangleAlert className="h-3.5 w-3.5" /></span>
                  <span className="truncate">{w.itemName}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted">{w.fieldLabel} · ~{w.bits} {t("bits")}</span>
                </button>
              ))}
            </div>
          )}

          {report.reused.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold tracking-wide text-muted">{t("REUSED PASSWORDS")}</div>
              {report.reused.map((g, i) => (
                <div key={i} className="mb-2 rounded border border-border">
                  <div className="border-b border-border px-2 py-1 text-xs text-muted">{t("Used by")} {g.items.length} {t("items")}</div>
                  {g.items.map((e, j) => (
                    <button key={j} onClick={() => pick(e.itemId)} className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-panel2">
                      <span className="text-danger"><Recycle className="h-3.5 w-3.5" /></span>
                      <span className="truncate">{e.itemName}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted">{e.fieldLabel}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
